import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Star, Briefcase, ClipboardCheck, Shield, 
  TrendingUp, Award, Users, Calendar, FileText, Flag, Sparkles
} from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <Card className="border-0 shadow-lg">
    <CardHeader className="pb-3">
      <CardTitle className="font-heading text-lg flex items-center gap-2">
        <Icon size={20} className="text-[#E6007E]" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="prose prose-sm max-w-none text-[#2A1514]">{children}</CardContent>
  </Card>
);

export default function UserGuidePage() {
  const { language } = useLanguage();
  const el = language === 'el';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up max-w-4xl mx-auto" data-testid="user-guide">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2A1514]">
            {el ? 'Οδηγός Χρήσης' : 'User Guide'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {el ? 'Πώς λειτουργεί το GrowPlay Facilitator Hub' : 'How GrowPlay Facilitator Hub works'}
          </p>
        </div>

        {/* Evaluation System */}
        <Section icon={ClipboardCheck} title={el ? 'Σύστημα Αξιολόγησης' : 'Evaluation System'}>
          <p>
            {el
              ? 'Μετά από κάθε event/workshop, οι facilitators αξιολογούνται σε 15 κριτήρια, χωρισμένα σε 5 κατηγορίες, με βαθμολογία 1-5:'
              : 'After each event/workshop, facilitators are evaluated on 15 criteria across 5 categories, scored 1-5:'}
          </p>
          <div className="space-y-3 mt-3">
            <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
              <p className="font-semibold text-sm">{el ? '1. Επικοινωνία & Διαχείριση Ενέργειας' : '1. Communication & Energy Management'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Σαφήνεια οδηγιών, θετική ενέργεια, διαχείριση χρόνου' : 'Clear instructions, positive energy, time management'}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="font-semibold text-sm">{el ? '2. Διαχείριση Ομάδας & Ασφάλεια' : '2. Team Handling & Safety'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Ενεργοποίηση ομάδας, ασφάλεια συμμετεχόντων, διαχείριση κρίσεων' : 'Team engagement, participant safety, crisis management'}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="font-semibold text-sm">{el ? '3. Επαγγελματισμός & Αξιοπιστία' : '3. Professionalism & Reliability'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Έγκαιρη παρουσία, εμφάνιση, συνεργασία' : 'Timely presence, appearance, collaboration'}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="font-semibold text-sm">{el ? '4. Λειτουργική Εκτέλεση' : '4. Operational Execution'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Εγκατάσταση εξοπλισμού, τήρηση διαδικασιών, διαχείριση εξοπλισμού' : 'Equipment setup, procedure adherence, equipment management'}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
              <p className="font-semibold text-sm">{el ? '5. Δεξιότητες Facilitation GrowPlay®' : '5. GrowPlay® Facilitation Skills'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Εκπαιδευτικοί στόχοι, σύνδεση θεωρίας-πράξης, debriefing' : 'Educational goals, theory-practice connection, debriefing'}</p>
            </div>
          </div>
          <p className="mt-3 text-sm">
            {el
              ? 'Η 5η κατηγορία (Facilitation Skills) είναι προαιρετική και εφαρμόζεται μόνο σε workshops, όχι σε teambuilding events.'
              : 'Category 5 (Facilitation Skills) is optional and applies only to workshops, not teambuilding events.'}
          </p>
        </Section>

        {/* Evaluation Types */}
        <Section icon={Users} title={el ? 'Τύποι Αξιολόγησης' : 'Evaluation Types'}>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Badge className="bg-blue-100 text-blue-700 mt-0.5">{el ? 'Αυτοαξιολόγηση' : 'Self'}</Badge>
              <p className="text-sm">{el ? 'Αξιολογείς τον εαυτό σου μετά από κάθε event. Διαθέσιμο σε όλους τους ρόλους.' : 'Evaluate yourself after each event. Available to all roles.'}</p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Badge className="bg-purple-100 text-purple-700 mt-0.5">{el ? 'Εποπτεία' : 'Supervisor'}</Badge>
              <p className="text-sm">{el ? 'Οι Senior Facilitators αξιολογούν τους Facilitators που επόπτευσαν στο event.' : 'Senior Facilitators evaluate the Facilitators they supervised at the event.'}</p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Badge className="bg-green-100 text-green-700 mt-0.5">{el ? 'Ομότιμη' : 'Peer'}</Badge>
              <p className="text-sm">{el ? 'Αξιολόγηση μεταξύ ομότιμων. Διαθέσιμο μόνο όταν ο Admin το ενεργοποιήσει από τη Διαχείριση.' : 'Peer-to-peer evaluation. Available only when the Admin enables it from the Admin Panel.'}</p>
            </div>
          </div>
        </Section>

        {/* Score Badge System */}
        <Section icon={Shield} title={el ? 'Σύστημα Badges Βαθμολογίας' : 'Score Badge System'}>
          <p className="mb-3">
            {el
              ? 'Ο μέσος όρος αξιολογήσεων καθορίζει το χρωματικό badge κάθε facilitator:'
              : 'The average evaluation score determines each facilitator\'s color badge:'}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">4+</span>
              </div>
              <div>
                <p className="font-semibold text-green-700 text-sm">{el ? 'Πράσινο' : 'Green'} — {el ? 'Εξαιρετική Απόδοση' : 'Excellent Performance'}</p>
                <p className="text-xs text-gray-600">{el ? 'Μέσος όρος ≥ 4.0/5' : 'Average score ≥ 4.0/5'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">2.5</span>
              </div>
              <div>
                <p className="font-semibold text-yellow-700 text-sm">{el ? 'Κίτρινο' : 'Yellow'} — {el ? 'Χρειάζεται Βελτίωση' : 'Needs Improvement'}</p>
                <p className="text-xs text-gray-600">{el ? 'Μέσος όρος ≥ 2.5 και < 4.0' : 'Average score ≥ 2.5 and < 4.0'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">&lt;2.5</span>
              </div>
              <div>
                <p className="font-semibold text-red-700 text-sm">{el ? 'Κόκκινο' : 'Red'} — {el ? 'Κρίσιμη Κατάσταση' : 'Critical Status'}</p>
                <p className="text-xs text-gray-600">{el ? 'Μέσος όρος < 2.5/5' : 'Average score < 2.5/5'}</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Development Flags on Evaluations */}
        <Section icon={Flag} title={el ? 'Σημαίες Ανάπτυξης ανά Κριτήριο' : 'Per-Criterion Development Flags'}>
          <p className="mb-3 text-sm">
            {el
              ? 'Όταν ένα συγκεκριμένο κριτήριο σε μια αξιολόγηση βαθμολογείται με 1 ή 2 (στα 5), εμφανίζεται μια χρωματιστή σημαία δίπλα στην αξιολόγηση που ονομάζει το κριτήριο. Στόχος είναι να εντοπίζονται γρήγορα οι επαναλαμβανόμενες αδυναμίες, ώστε να γίνονται στοχευμένες ενέργειες ανάπτυξης.'
              : 'When a single criterion in an evaluation is scored 1 or 2 (out of 5), a colored flag appears on that evaluation naming the criterion. The goal is to surface recurring weaknesses fast so we can plan targeted development.'}
          </p>
          <p className="mb-3 text-sm">
            {el
              ? 'Η ένταση της σημαίας κλιμακώνεται με βάση πόσες φορές το ίδιο κριτήριο έχει σκοράρει χαμηλά για το ίδιο άτομο, χρονολογικά:'
              : 'The flag escalates based on how many times the same criterion has scored low for the same person, chronologically:'}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <Flag size={18} className="text-yellow-600 fill-yellow-500" />
              <div>
                <p className="font-semibold text-yellow-800 text-sm">
                  {el ? 'Κίτρινη — 1η εμφάνιση' : 'Yellow — 1st occurrence'}
                </p>
                <p className="text-xs text-gray-600">
                  {el
                    ? 'Πρώτη φορά που εμφανίζεται χαμηλή βαθμολογία (1-2) στο συγκεκριμένο κριτήριο. Σήμα προσοχής, χωρίς συναγερμό.'
                    : 'First time a low score (1-2) appears on this specific criterion. Heads-up, no alarm.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <Flag size={18} className="text-orange-600 fill-orange-500" />
              <div>
                <p className="font-semibold text-orange-800 text-sm">
                  {el ? 'Πορτοκαλί — 2η εμφάνιση' : 'Orange — 2nd occurrence'}
                </p>
                <p className="text-xs text-gray-600">
                  {el
                    ? 'Το ίδιο κριτήριο σκόραρε χαμηλά ξανά. Επαναλαμβανόμενο μοτίβο — αξίζει συζήτηση και υποστήριξη.'
                    : 'The same criterion scored low again. Pattern emerging — worth a coaching conversation.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <Flag size={18} className="text-red-600 fill-red-500" />
              <div>
                <p className="font-semibold text-red-800 text-sm">
                  {el ? 'Κόκκινη — 3η ή περισσότερες εμφανίσεις' : 'Red — 3rd or more occurrences'}
                </p>
                <p className="text-xs text-gray-600">
                  {el
                    ? 'Σαφής αδυναμία ανάπτυξης. Η ομάδα πρέπει να σχεδιάσει στοχευμένη εκπαίδευση γι\' αυτό το κριτήριο.'
                    : 'Clear development gap. Team should plan focused training for this criterion.'}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-pink-50 rounded-lg border border-pink-100">
            <p className="text-sm flex items-start gap-2">
              <Sparkles size={16} className="text-[#E6007E] mt-0.5 shrink-0" />
              <span>
                {el
                  ? <><strong>Προφίλ Ανάπτυξης (Admin):</strong> Στη Διαχείριση Χρηστών, πατώντας το εικονίδιο ✨ δίπλα σε κάθε άτομο, βλέπεις σύνοψη των top-3 αδυναμιών του και προτεινόμενα workshops από τη Βιβλιοθήκη που στοχεύουν αυτά τα κριτήρια. Μπορείς να αναθέσεις workshop με ένα κλικ — ο facilitator το βλέπει στην καρτέλα «Συμμετοχές → Προτεινόμενα για σένα».</>
                  : <><strong>Development Profile (Admin):</strong> In User Management, click the ✨ icon next to a person to see their top-3 weaknesses and the workshops in the Library that address those criteria. Assign a workshop with one click — the facilitator sees it under "Participation → Recommended for you".</>}
              </span>
            </p>
          </div>
        </Section>

        {/* Top Performers / Leaderboard */}
        <Section icon={TrendingUp} title={el ? 'Κατάταξη — Top Performers' : 'Ranking — Top Performers'}>
          <p>
            {el
              ? 'Η κατάταξη βασίζεται στον μέσο όρο αξιολογήσεων κάθε χρήστη στη συγκεκριμένη χρονική περίοδο. Υπάρχουν δύο ξεχωριστές λίστες:'
              : 'The ranking is based on each user\'s average evaluation score in the selected time period. There are two separate lists:'}
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            <li><strong>Top Performers — Facilitators:</strong> {el ? 'Μόνο facilitators, ταξινομημένοι κατά μέσο όρο βαθμολογίας (υψηλότερος πρώτα).' : 'Facilitators only, sorted by average score (highest first).'}</li>
            <li><strong>Top Performers — Senior Facilitators:</strong> {el ? 'Μόνο Senior Facilitators, ξεχωριστή κατάταξη.' : 'Senior Facilitators only, ranked separately.'}</li>
          </ul>
        </Section>

        {/* MVP Awards */}
        <Section icon={Trophy} title={el ? 'MVP Βραβεία Τριμήνου' : 'Quarterly MVP Awards'}>
          <p>
            {el
              ? 'Τα βραβεία υπολογίζονται αυτόματα (ή χειροκίνητα) ανά τρίμηνο. Μόνο Facilitators είναι επιλέξιμοι (εξαιρούνται Admins & Senior Facilitators).'
              : 'Awards are auto-calculated (or manually assigned) per quarter. Only Facilitators are eligible (Admins & Senior Facilitators are excluded).'}
          </p>
          <div className="space-y-3 mt-3">
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={16} className="text-yellow-600" />
                <p className="font-semibold text-sm">MVP Facilitator</p>
              </div>
              <p className="text-xs text-gray-600">
                {el
                  ? 'Ο facilitator με τον υψηλότερο συνολικό μέσο όρο σε ΟΛΕΣ τις κατηγορίες αξιολόγησης μέσα στο τρίμηνο.'
                  : 'The facilitator with the highest overall average across ALL evaluation categories in the quarter.'}
              </p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-1">
                <Star size={16} className="text-orange-600" />
                <p className="font-semibold text-sm">Best Energy</p>
              </div>
              <p className="text-xs text-gray-600">
                {el
                  ? 'Υψηλότερος μέσος όρος στα κριτήρια: Σαφής επικοινωνία οδηγιών, Θετική ενέργεια/ενθουσιασμός, Διαχείριση χρόνου.'
                  : 'Highest average in: Clear instruction communication, Positive energy/enthusiasm, Time management.'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Award size={16} className="text-blue-600" />
                <p className="font-semibold text-sm">Best Reliability</p>
              </div>
              <p className="text-xs text-gray-600">
                {el
                  ? 'Υψηλότερος μέσος όρος στα κριτήρια: Έγκαιρη παρουσία, Εμφάνιση & dress code, Συνεργασία με Συντονιστή.'
                  : 'Highest average in: Timely presence, Appearance & dress code, Collaboration with Coordinator.'}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <Star size={16} className="text-purple-600" />
                <p className="font-semibold text-sm">Best Debriefer</p>
              </div>
              <p className="text-xs text-gray-600">
                {el
                  ? 'Υψηλότερος μέσος όρος στα κριτήρια: Καθοδήγηση προς εκπαιδευτικούς στόχους, Σύνδεση θεωρίας-πράξης, Δεξιότητες debriefing.'
                  : 'Highest average in: Guiding towards educational goals, Theory-practice connection, Debriefing skills.'}
              </p>
            </div>
          </div>
        </Section>

        {/* Works/Month */}
        <Section icon={Briefcase} title={el ? 'Εργασίες / Μήνα (Works/Month)' : 'Works / Month Ranking'}>
          <p>
            {el
              ? 'Αυτή η κατάταξη βοηθάει τον Ops Manager να δει αν υπάρχουν facilitators που δεν παίρνουν αρκετή δουλειά σε σύγκριση με τους υπόλοιπους, ώστε να τους αναθέσει περισσότερα events.'
              : 'This ranking helps the Ops Manager identify facilitators who aren\'t getting enough work compared to others, so they can assign them more events.'}
          </p>

          <p className="font-semibold mt-3 text-sm">{el ? 'Πώς μετράνε τα events:' : 'How events are counted:'}</p>
          <p className="text-sm">
            {el
              ? 'Μετράμε τα μοναδικά events στα οποία ο facilitator/senior έλαβε αξιολόγηση (ως αξιολογούμενος). Αυτός είναι ο πιο αξιόπιστος δείκτης συμμετοχής.'
              : 'We count the unique events where the facilitator/senior received an evaluation (as evaluatee). This is the most reliable participation indicator.'}
          </p>

          <p className="font-semibold mt-3 text-sm">{el ? 'Πώς υπολογίζεται ο δείκτης events/μήνα:' : 'How events/month is calculated:'}</p>
          <p className="text-sm">
            {el
              ? 'Σύνολο events ÷ μήνες στη χρονική περίοδο = events/μήνα'
              : 'Total events ÷ months in time period = events/month'}
          </p>

          <p className="font-semibold mt-3 text-sm">{el ? 'Χρωματικό Status:' : 'Color Status:'}</p>
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-3 p-2.5 bg-green-50 rounded-lg border border-green-200">
              <div className="w-6 h-6 rounded-full bg-green-500" />
              <div>
                <p className="font-semibold text-green-700 text-sm">{el ? 'Πράσινο — Κανονική συμμετοχή' : 'Green — Normal participation'}</p>
                <p className="text-xs text-gray-600">{el ? 'Events/μήνα ≥ μέσος όρος ομάδας' : 'Events/month ≥ group average'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2.5 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-6 h-6 rounded-full bg-orange-500" />
              <div>
                <p className="font-semibold text-orange-700 text-sm">{el ? 'Πορτοκαλί — Κάτω του μέσου όρου' : 'Orange — Below average'}</p>
                <p className="text-xs text-gray-600">{el ? 'Events/μήνα < μέσος όρος ομάδας (αλλά > 1)' : 'Events/month < group average (but > 1)'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2.5 bg-red-50 rounded-lg border border-red-200">
              <div className="w-6 h-6 rounded-full bg-red-500" />
              <div>
                <p className="font-semibold text-red-700 text-sm">{el ? 'Κόκκινο — Χαμηλή συμμετοχή' : 'Red — Low participation'}</p>
                <p className="text-xs text-gray-600">{el ? 'Events/μήνα ≤ 1' : 'Events/month ≤ 1'}</p>
              </div>
            </div>
          </div>
          <p className="text-sm mt-3">
            {el
              ? 'Οι Facilitators και Senior Facilitators κατατάσσονται ξεχωριστά, γιατί οι φόρτοι εργασίας δεν είναι συγκρίσιμοι μεταξύ τους. Κάθε ομάδα έχει τον δικό της μέσο όρο.'
              : 'Facilitators and Senior Facilitators are ranked separately since their workloads aren\'t comparable. Each group has its own average.'}
          </p>
        </Section>

        {/* Reports */}
        <Section icon={FileText} title={el ? 'Αναφορές & Εξαγωγές' : 'Reports & Exports'}>
          <div className="space-y-2 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold">{el ? 'Μηνιαία Αναφορά Προόδου (PDF)' : 'Monthly Progress Report (PDF)'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Κάθε facilitator μπορεί να κατεβάσει τη δική του μηνιαία αναφορά με βαθμολογίες και τάσεις.' : 'Each facilitator can download their own monthly report with scores and trends.'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold">{el ? 'Αναφορά Εργασιών/Μήνα (PDF)' : 'Works/Month Report (PDF)'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Μόνο Admin. Εξάγει την κατάταξη εργασιών/μήνα για όλους τους facilitators και seniors.' : 'Admin only. Exports the works/month ranking for all facilitators and seniors.'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold">{el ? 'Εξαγωγή Αξιολογήσεων (Excel)' : 'Evaluations Export (Excel)'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Μόνο Admin. Εξάγει όλες τις αξιολογήσεις σε αρχείο Excel.' : 'Admin only. Exports all evaluations to an Excel file.'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold">{el ? 'PDF Ατομικής Αξιολόγησης' : 'Individual Evaluation PDF'}</p>
              <p className="text-xs text-gray-600 mt-1">{el ? 'Κάθε αξιολόγηση μπορεί να εξαχθεί σε PDF με πλήρη ανάλυση βαθμολογιών ανά κατηγορία.' : 'Each evaluation can be exported to PDF with full category score breakdown.'}</p>
            </div>
          </div>
        </Section>

        {/* Roles */}
        <Section icon={Users} title={el ? 'Ρόλοι Χρηστών' : 'User Roles'}>
          <div className="space-y-2">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="font-semibold text-sm text-purple-700">Admin</p>
              <p className="text-xs text-gray-600 mt-1">
                {el
                  ? 'Πλήρης πρόσβαση: Δημιουργία events, διαχείριση χρηστών, προβολή όλων αξιολογήσεων, εξαγωγή αναφορών, υπολογισμός βραβείων, on/off ομότιμης αξιολόγησης.'
                  : 'Full access: Create events, manage users, view all evaluations, export reports, calculate awards, toggle peer evaluation.'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="font-semibold text-sm text-blue-700">Senior Facilitator</p>
              <p className="text-xs text-gray-600 mt-1">
                {el
                  ? 'Αξιολόγηση εαυτού και facilitators (εποπτεία). Προβολή αιτημάτων εκπαίδευσης. Δεν μπορεί να δημιουργήσει events.'
                  : 'Self-evaluate and supervisor-evaluate facilitators. View training requests. Cannot create events.'}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="font-semibold text-sm text-green-700">Facilitator</p>
              <p className="text-xs text-gray-600 mt-1">
                {el
                  ? 'Αυτοαξιολόγηση μετά από events. Ομότιμη αξιολόγηση μόνο αν ο Admin την ενεργοποιήσει. Προβολή προσωπικών στατιστικών και αναφορών.'
                  : 'Self-evaluate after events. Peer evaluation only if Admin enables it. View personal stats and reports.'}
              </p>
            </div>
          </div>
        </Section>

        <p className="text-center text-xs text-gray-400 py-4">
          GrowPlay Facilitator Hub — Summit.gr
        </p>
      </div>
    </Layout>
  );
}
